import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useOperator } from '@/lib/operator-context';
import {
  getOrders,
  updateOrderStatus,
  updateOrderPayment,
  updateDeliveryStatus,
  createOrder,
  searchCatalogProducts,
  Order,
  OrderListResponse,
  CatalogProduct,
  CreateOrderLineItem,
} from '@/lib/orders-service';
import { searchCustomers } from '@/lib/operator-api';
import type { OperatorCustomer } from '@/lib/operator-api/types';
import {
  OrderStatus,
  OrderPaymentStatus,
  FulfillmentType,
  DeliveryStatus,
  OrderFilters,
  OrderStats,
} from '@/types/orders';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Search, Eye, CreditCard, CheckCircle, ExternalLink, Plus, Trash2, Package, Wrench, Truck, MapPin } from 'lucide-react';

export const Route = createFileRoute('/_auth/operator/orders/')({
  component: OperatorOrdersPage,
});

// ============================================================================
// Extended Order Type for Display (adds computed fields)
// ============================================================================

interface DisplayOrder extends Order {
  /** Computed: total item count */
  itemCount: number;
  /** Computed: amount still due */
  amountDue: number;
}

// ============================================================================
// Badge variant mapping helpers
// ============================================================================

function getOrderStatusBadgeVariant(status: OrderStatus): "pending" | "confirmed" | "inProgress" | "completed" | "cancelled" | "secondary" {
  const map: Record<OrderStatus, "pending" | "confirmed" | "inProgress" | "completed" | "cancelled" | "secondary"> = {
    PENDING: 'pending',
    PROCESSING: 'inProgress',
    READY: 'confirmed',
    COMPLETED: 'completed',
    CANCELLED: 'cancelled',
  };
  return map[status];
}

function getPaymentStatusBadgeVariant(status: OrderPaymentStatus): "paid" | "unpaid" | "refunded" | "pending" | "secondary" {
  const map: Record<OrderPaymentStatus, "paid" | "unpaid" | "refunded" | "pending" | "secondary"> = {
    PAID: 'paid',
    UNPAID: 'unpaid',
    PARTIALLY_PAID: 'pending',
    REFUNDED: 'refunded',
    VOIDED: 'secondary',
  };
  return map[status];
}

function getFulfillmentBadgeVariant(type: FulfillmentType): "default" | "secondary" | "outline" {
  const map: Record<FulfillmentType, "default" | "secondary" | "outline"> = {
    PICKUP: 'secondary',
    DELIVERY: 'default',
    IN_STORE: 'outline',
  };
  return map[type];
}

function getDeliveryStatusBadgeVariant(status: DeliveryStatus): "pending" | "inProgress" | "confirmed" | "completed" {
  const map: Record<DeliveryStatus, "pending" | "inProgress" | "confirmed" | "completed"> = {
    PENDING: 'pending',
    OUT_FOR_DELIVERY: 'inProgress',
    ARRIVED: 'confirmed',
    DELIVERED: 'completed',
  };
  return map[status];
}

function getNextDeliveryStatus(current: DeliveryStatus): DeliveryStatus | null {
  const next: Record<DeliveryStatus, DeliveryStatus | null> = {
    PENDING: 'OUT_FOR_DELIVERY',
    OUT_FOR_DELIVERY: 'ARRIVED',
    ARRIVED: 'DELIVERED',
    DELIVERED: null,
  };
  return next[current];
}

function getDeliveryActionLabel(next: DeliveryStatus): string {
  const labels: Record<DeliveryStatus, string> = {
    PENDING: '',
    OUT_FOR_DELIVERY: 'Saiu para entrega',
    ARRIVED: 'Chegou',
    DELIVERED: 'Entregue',
  };
  return labels[next];
}

// ============================================================================
// Component
// ============================================================================

function OperatorOrdersPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { operator, isLoading: authLoading, selectedBusiness } = useOperator();

  // Order data state
  const [orders, setOrders] = useState<DisplayOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Inline error feedback state (replaces alert() calls)
  const [createError, setCreateError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // New Order dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [newOrderCustomerId, setNewOrderCustomerId] = useState('');
  const [newOrderCustomerName, setNewOrderCustomerName] = useState('');
  const [newOrderNotes, setNewOrderNotes] = useState('');
  const [newOrderFulfillment, setNewOrderFulfillment] = useState<FulfillmentType>('IN_STORE');
  const [newOrderDeliveryAddress, setNewOrderDeliveryAddress] = useState('');
  const [newOrderDeliveryNotes, setNewOrderDeliveryNotes] = useState('');
  const [newOrderDeliveryContactName, setNewOrderDeliveryContactName] = useState('');
  const [newOrderDeliveryContactPhone, setNewOrderDeliveryContactPhone] = useState('');
  const [newOrderLineItems, setNewOrderLineItems] = useState<Array<CreateOrderLineItem & { displayName: string; displayType: 'SERVICE' | 'PRODUCT' }>>([]);

  // Customer search state
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState<OperatorCustomer[]>([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const customerSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Product search state
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState<CatalogProduct[]>([]);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const productSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Filter state
  const [filters, setFilters] = useState<OrderFilters>({
    status: 'ALL',
    paymentStatus: 'ALL',
    fulfillmentType: 'ALL',
    searchQuery: '',
  });

  // Transform Order to DisplayOrder with computed fields
  const transformToDisplayOrder = useCallback((order: Order): DisplayOrder => {
    const itemCount = order.lineItems.reduce((sum, item) => sum + item.quantity, 0);
    const amountDue = order.total - order.paidAmount;
    return {
      ...order,
      itemCount,
      amountDue,
    };
  }, []);

  // Fetch orders from API
  const fetchOrders = useCallback(async (signal?: AbortSignal) => {
    setIsLoading(true);
    setError(null);
    try {
      const apiFilters: OrderFilters = {
        ...filters,
        businessId: selectedBusiness?.id,
      };
      const response: OrderListResponse = await getOrders(apiFilters);
      if (signal?.aborted) return;
      const displayOrders = response.orders.map(transformToDisplayOrder);
      setOrders(displayOrders);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      if (signal?.aborted) return;
      console.error('Failed to fetch orders:', err);
      setError('Failed to load orders. Please try again.');
    } finally {
      if (!signal?.aborted) setIsLoading(false);
    }
  }, [filters, selectedBusiness?.id, transformToDisplayOrder]);

  // Fetch orders on mount and when filters change
  useEffect(() => {
    const controller = new AbortController();
    fetchOrders(controller.signal);
    return () => controller.abort();
  }, [fetchOrders]);

  // Computed filtered orders (client-side filtering for search)
  const filteredOrders = useMemo(() => {
    // Search query filtering is done client-side for responsiveness
    if (!filters.searchQuery) {
      return orders;
    }

    const query = filters.searchQuery.toLowerCase();
    return orders.filter((order) => {
      const matchesOrderNumber = order.orderNumber.toLowerCase().includes(query);
      const matchesCustomerName = order.customerName.toLowerCase().includes(query);
      return matchesOrderNumber || matchesCustomerName;
    });
  }, [orders, filters.searchQuery]);

  // Computed stats
  const stats: OrderStats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const todaysOrders = orders.filter((order) => {
      const orderDate = new Date(order.createdAt);
      orderDate.setHours(0, 0, 0, 0);
      return orderDate.getTime() === today.getTime();
    });

    const pendingOrders = orders.filter(
      (order) => order.status === 'PENDING' || order.status === 'PROCESSING'
    );

    const outstandingPayments = orders.reduce(
      (sum, order) => sum + order.amountDue,
      0
    );

    const todayRevenue = todaysOrders
      .filter((order) => order.status !== 'CANCELLED')
      .reduce((sum, order) => sum + order.paidAmount, 0);

    return {
      totalOrders: todaysOrders.length,
      pendingOrders: pendingOrders.length,
      outstandingPayments,
      todayRevenue,
    };
  }, [orders]);

  // Customer search with debounce
  const handleCustomerSearch = useCallback((query: string) => {
    setCustomerSearch(query);
    if (customerSearchTimer.current) clearTimeout(customerSearchTimer.current);
    if (!query || query.length < 2) {
      setCustomerResults([]);
      setShowCustomerDropdown(false);
      return;
    }
    customerSearchTimer.current = setTimeout(async () => {
      try {
        const res = await searchCustomers(query, selectedBusiness?.id);
        setCustomerResults(res.customers || []);
        setShowCustomerDropdown(true);
      } catch {
        setCustomerResults([]);
      }
    }, 300);
  }, [selectedBusiness?.id]);

  const handleSelectCustomer = (customer: OperatorCustomer) => {
    setNewOrderCustomerId(customer.id);
    setNewOrderCustomerName(customer.name || customer.phone);
    setCustomerSearch(customer.name || customer.phone);
    setShowCustomerDropdown(false);
  };

  // Product search with debounce
  const handleProductSearch = useCallback((query: string) => {
    setProductSearch(query);
    if (productSearchTimer.current) clearTimeout(productSearchTimer.current);
    if (!selectedBusiness?.id) return;
    productSearchTimer.current = setTimeout(async () => {
      try {
        const products = await searchCatalogProducts(selectedBusiness.id, query, 10);
        setProductResults(products);
        setShowProductDropdown(true);
      } catch {
        setProductResults([]);
      }
    }, 300);
  }, [selectedBusiness?.id]);

  const handleSelectProduct = (product: CatalogProduct) => {
    setNewOrderLineItems((prev) => [
      ...prev,
      {
        catalogProductId: product.id,
        unitPrice: product.priceCents / 100,
        quantity: 1,
        displayName: product.name,
        displayType: 'PRODUCT',
      },
    ]);
    setProductSearch('');
    setShowProductDropdown(false);
  };

  const handleAddManualItem = () => {
    setNewOrderLineItems((prev) => [
      ...prev,
      {
        description: '',
        unitPrice: 0,
        quantity: 1,
        displayName: '',
        displayType: 'SERVICE',
      },
    ]);
  };

  const handleRemoveLineItem = (index: number) => {
    setNewOrderLineItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleLineItemChange = (index: number, field: string, value: string | number) => {
    setNewOrderLineItems((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;
        if (field === 'description') return { ...item, description: value as string, displayName: value as string };
        if (field === 'unitPrice') return { ...item, unitPrice: Number(value) };
        if (field === 'quantity') return { ...item, quantity: Math.max(1, Number(value)) };
        return item;
      })
    );
  };

  const resetCreateDialog = () => {
    setNewOrderCustomerId('');
    setNewOrderCustomerName('');
    setNewOrderNotes('');
    setNewOrderFulfillment('IN_STORE');
    setNewOrderDeliveryAddress('');
    setNewOrderDeliveryNotes('');
    setNewOrderDeliveryContactName('');
    setNewOrderDeliveryContactPhone('');
    setNewOrderLineItems([]);
    setCustomerSearch('');
    setProductSearch('');
    setCustomerResults([]);
    setProductResults([]);
    setShowCustomerDropdown(false);
    setShowProductDropdown(false);
  };

  const handleCreateOrder = async () => {
    if (!selectedBusiness?.id || !newOrderCustomerId || newOrderLineItems.length === 0) return;

    setCreateLoading(true);
    setCreateError(null);
    try {
      const lineItems: CreateOrderLineItem[] = newOrderLineItems.map((item) => {
        if (item.catalogProductId) {
          return { catalogProductId: item.catalogProductId, quantity: item.quantity, unitPrice: item.unitPrice };
        }
        return { description: item.description || 'Service', unitPrice: item.unitPrice || 0, quantity: item.quantity };
      });

      const result = await createOrder({
        businessId: selectedBusiness.id,
        customerId: newOrderCustomerId,
        customerName: newOrderCustomerName,
        lineItems,
        notes: newOrderNotes || undefined,
        fulfillmentType: newOrderFulfillment,
        ...(newOrderFulfillment === 'DELIVERY' ? {
          deliveryAddress: newOrderDeliveryAddress || undefined,
          deliveryNotes: newOrderDeliveryNotes || undefined,
          deliveryContactName: newOrderDeliveryContactName || undefined,
          deliveryContactPhone: newOrderDeliveryContactPhone || undefined,
        } : {}),
      });

      if (result.success) {
        setShowCreateDialog(false);
        resetCreateDialog();
        setCreateError(null);
        await fetchOrders();
      } else {
        const msg = result.error || 'Failed to create order';
        console.error('Create order failed:', msg);
        setCreateError(msg);
      }
    } catch (err) {
      console.error('Create order error:', err);
      setCreateError('Failed to create order');
    } finally {
      setCreateLoading(false);
    }
  };

  // Handlers
  const handleFilterChange = (key: keyof OrderFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleMarkPaid = async (orderId: string) => {
    setActionError(null);
    const order = orders.find((o) => o.id === orderId);
    if (!order) return;

    const result = await updateOrderPayment(orderId, 'PAID', order.total, 'CASH');
    if (result.success) {
      // Refresh orders to get updated data
      await fetchOrders();
    } else {
      const msg = `Failed to mark order as paid: ${result.error}`;
      console.error(msg);
      setActionError(msg);
    }
  };

  const handleMarkReady = async (orderId: string) => {
    setActionError(null);
    const result = await updateOrderStatus(orderId, 'READY');
    if (result.success) {
      // Refresh orders to get updated data
      await fetchOrders();
    } else {
      const msg = `Failed to mark order as ready: ${result.error}`;
      console.error(msg);
      setActionError(msg);
    }
  };

  const handleAdvanceDelivery = async (orderId: string, currentStatus: DeliveryStatus) => {
    setActionError(null);
    const next = getNextDeliveryStatus(currentStatus);
    if (!next) return;

    const result = await updateDeliveryStatus(orderId, next);
    if (result.success) {
      await fetchOrders();
    } else {
      const msg = `Failed to update delivery: ${result.error}`;
      console.error(msg);
      setActionError(msg);
    }
  };

  const handleViewOrder = (orderId: string) => {
    navigate({ to: `/operator/orders/${orderId}` });
  };

  const handleViewPayment = (paymentId: string) => {
    navigate({ to: `/operator/payments/${paymentId}` });
  };

  // Format helpers
  const formatCurrency = (amount: number, currency: string = 'BRL') => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  const formatTime = (date: Date) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatStatus = (status: string) => {
    return status.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
  };

  // Loading state
  if (authLoading || isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Auth check
  if (!operator) {
    return null;
  }

  // Error state
  if (error) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-16 text-center">
            <div className="text-4xl mb-4">Error</div>
            <h3 className="text-lg font-semibold text-foreground">{error}</h3>
            <Button onClick={() => fetchOrders()} className="mt-4">
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('orders.title')}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t('orders.orderCount', { count: filteredOrders.length })} | {t('common.viewing')}{' '}
            <strong>{selectedBusiness ? selectedBusiness.name : t('common.allBusinesses')}</strong>
          </p>
        </div>
        {selectedBusiness && (
          <Button onClick={() => { resetCreateDialog(); setShowCreateDialog(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            {t('orders.newOrder')}
          </Button>
        )}
      </div>

      {/* Filter Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs font-medium text-muted-foreground mb-1 block">{t('orders.status')}</label>
              <Select
                value={filters.status || 'ALL'}
                onValueChange={(value) => handleFilterChange('status', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('orders.allStatuses')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">{t('orders.allStatuses')}</SelectItem>
                  <SelectItem value="PENDING">{t('orders.pending')}</SelectItem>
                  <SelectItem value="PROCESSING">{t('orders.processing')}</SelectItem>
                  <SelectItem value="READY">{t('orders.ready')}</SelectItem>
                  <SelectItem value="COMPLETED">{t('orders.completed')}</SelectItem>
                  <SelectItem value="CANCELLED">{t('orders.cancelled')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1 min-w-[200px]">
              <label className="text-xs font-medium text-muted-foreground mb-1 block">{t('orders.payment')}</label>
              <Select
                value={filters.paymentStatus || 'ALL'}
                onValueChange={(value) => handleFilterChange('paymentStatus', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('orders.allPayments')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">{t('orders.allPayments')}</SelectItem>
                  <SelectItem value="UNPAID">{t('orders.unpaid')}</SelectItem>
                  <SelectItem value="PARTIALLY_PAID">{t('orders.partiallyPaid')}</SelectItem>
                  <SelectItem value="PAID">{t('orders.paid')}</SelectItem>
                  <SelectItem value="REFUNDED">{t('orders.refunded')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1 min-w-[200px]">
              <label className="text-xs font-medium text-muted-foreground mb-1 block">{t('orders.fulfillment')}</label>
              <Select
                value={filters.fulfillmentType || 'ALL'}
                onValueChange={(value) => handleFilterChange('fulfillmentType', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('orders.allTypes')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">{t('orders.allTypes')}</SelectItem>
                  <SelectItem value="PICKUP">{t('orders.pickup')}</SelectItem>
                  <SelectItem value="DELIVERY">{t('orders.delivery')}</SelectItem>
                  <SelectItem value="IN_STORE">{t('orders.inStore')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex-[2] min-w-[250px]">
              <label className="text-xs font-medium text-muted-foreground mb-1 block">{t('common.search')}</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('orders.searchPlaceholder')}
                  value={filters.searchQuery || ''}
                  onChange={(e) => handleFilterChange('searchQuery', e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Error Banner */}
      {actionError && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <p className="text-destructive text-sm">{actionError}</p>
              <Button variant="ghost" size="sm" onClick={() => setActionError(null)}>
                {t('common.close')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">{t('orders.todaysOrders')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalOrders}</div>
            <p className="text-xs text-muted-foreground">{t('orders.ordersCreatedToday')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">{t('orders.pendingOrders')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">{stats.pendingOrders}</div>
            <p className="text-xs text-muted-foreground">{t('orders.awaitingProcessing')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">{t('orders.outstandingPayments')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-rose-600">{formatCurrency(stats.outstandingPayments)}</div>
            <p className="text-xs text-muted-foreground">{t('orders.totalAmountDue')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">{t('orders.todaysRevenue')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">{formatCurrency(stats.todayRevenue)}</div>
            <p className="text-xs text-muted-foreground">{t('orders.paymentsReceived')}</p>
          </CardContent>
        </Card>
      </div>

      {/* Orders Table */}
      {filteredOrders.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <h3 className="text-lg font-semibold text-foreground">{t('orders.noOrdersFound')}</h3>
            <p className="text-sm text-muted-foreground">{t('orders.tryAdjustingFilters')}</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('orders.orderNumber')}</TableHead>
                <TableHead>{t('orders.customer')}</TableHead>
                <TableHead className="text-center">{t('orders.items')}</TableHead>
                <TableHead>{t('orders.total')}</TableHead>
                <TableHead>{t('orders.status')}</TableHead>
                <TableHead>{t('orders.payment')}</TableHead>
                <TableHead>{t('orders.fulfillment')}</TableHead>
                <TableHead>{t('orders.time')}</TableHead>
                <TableHead className="text-right">{t('orders.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredOrders.map((order) => (
                <TableRow key={order.id}>
                  <TableCell>
                    <div className="font-semibold font-mono text-sm">{order.orderNumber}</div>
                    {order.linkedAppointmentId && (
                      <div className="text-xs text-muted-foreground">{t('orders.linkedToAppointment')}</div>
                    )}
                  </TableCell>
                  <TableCell>
                    {order.customerId ? (
                      <button
                        className="font-medium text-left hover:text-primary hover:underline transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate({ to: `/operator/customers/${order.customerId}` });
                        }}
                      >
                        {order.customerName}
                      </button>
                    ) : (
                      <div className="font-medium">{order.customerName}</div>
                    )}
                    {order.customerPhone && (
                      <div className="text-sm text-muted-foreground">{order.customerPhone}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-muted text-sm font-semibold">
                      {order.itemCount}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="font-semibold">{formatCurrency(order.total)}</div>
                    {order.amountDue > 0 && (
                      <div className="text-sm text-rose-600">{t('orders.due')} {formatCurrency(order.amountDue)}</div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={getOrderStatusBadgeVariant(order.status)}>
                      {formatStatus(order.status)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Badge variant={getPaymentStatusBadgeVariant(order.paymentStatus)}>
                        {formatStatus(order.paymentStatus)}
                      </Badge>
                      {order.paymentStatus === 'PAID' && order.paymentId && (
                        <button
                          className="text-xs text-primary hover:underline flex items-center gap-1"
                          onClick={() => handleViewPayment(order.paymentId!)}
                          title="View payment details"
                        >
                          <ExternalLink className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <Badge variant={getFulfillmentBadgeVariant(order.fulfillmentType)}>
                        {formatStatus(order.fulfillmentType)}
                      </Badge>
                      {order.fulfillmentType === 'DELIVERY' && order.deliveryStatus && (
                        <div className="flex items-center gap-1">
                          <Truck className="h-3 w-3 text-muted-foreground" />
                          <Badge variant={getDeliveryStatusBadgeVariant(order.deliveryStatus)} className="text-xs">
                            {formatStatus(order.deliveryStatus)}
                          </Badge>
                        </div>
                      )}
                      {order.fulfillmentType === 'DELIVERY' && order.deliveryAddress && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <MapPin className="h-3 w-3 shrink-0" />
                          <span className="truncate max-w-[120px]">{order.deliveryAddress}</span>
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">{formatTime(order.createdAt)}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewOrder(order.id)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      {(order.paymentStatus === 'UNPAID' || order.paymentStatus === 'PARTIALLY_PAID') && (
                        <Button
                          variant="default"
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => handleMarkPaid(order.id)}
                        >
                          <CreditCard className="h-4 w-4 mr-1" />
                          {t('orders.markPaid')}
                        </Button>
                      )}
                      {order.status === 'PROCESSING' && (
                        <Button
                          variant="default"
                          size="sm"
                          className="bg-violet-600 hover:bg-violet-700"
                          onClick={() => handleMarkReady(order.id)}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          {t('orders.ready')}
                        </Button>
                      )}
                      {order.fulfillmentType === 'DELIVERY' && order.deliveryStatus && getNextDeliveryStatus(order.deliveryStatus) && (
                        <Button
                          variant="default"
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700"
                          onClick={() => handleAdvanceDelivery(order.id, order.deliveryStatus!)}
                        >
                          <Truck className="h-4 w-4 mr-1" />
                          {getDeliveryActionLabel(getNextDeliveryStatus(order.deliveryStatus)!)}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Create Order Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => { if (!open) resetCreateDialog(); setShowCreateDialog(open); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('orders.createOrder')}</DialogTitle>
            <DialogDescription>{selectedBusiness?.name}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Customer Search */}
            <div className="relative">
              <Label>{t('orders.customer')}</Label>
              <Input
                placeholder={t('orders.searchPlaceholder')}
                value={customerSearch}
                onChange={(e) => handleCustomerSearch(e.target.value)}
                onFocus={() => { if (customerResults.length > 0) setShowCustomerDropdown(true); }}
              />
              {newOrderCustomerId && (
                <p className="text-xs text-muted-foreground mt-1">{newOrderCustomerName}</p>
              )}
              {showCustomerDropdown && customerResults.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-40 overflow-y-auto">
                  {customerResults.map((c) => (
                    <button
                      key={c.id}
                      className="w-full text-left px-3 py-2 hover:bg-muted text-sm"
                      onClick={() => handleSelectCustomer(c)}
                    >
                      <span className="font-medium">{c.name || 'Unknown'}</span>
                      <span className="text-muted-foreground ml-2">{c.phone}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Line Items */}
            <div>
              <Label>{t('orders.lineItems')}</Label>
              <div className="space-y-2 mt-2">
                {newOrderLineItems.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 border rounded-md">
                    <Badge variant={item.displayType === 'PRODUCT' ? 'default' : 'secondary'} className="shrink-0">
                      {item.displayType === 'PRODUCT' ? (
                        <><Package className="h-3 w-3 mr-1" />{t('orders.product')}</>
                      ) : (
                        <><Wrench className="h-3 w-3 mr-1" />{t('orders.service')}</>
                      )}
                    </Badge>
                    {item.displayType === 'SERVICE' ? (
                      <Input
                        placeholder={t('orders.item')}
                        value={item.description || ''}
                        onChange={(e) => handleLineItemChange(idx, 'description', e.target.value)}
                        className="flex-1"
                      />
                    ) : (
                      <span className="flex-1 text-sm font-medium truncate">{item.displayName}</span>
                    )}
                    <Input
                      type="number"
                      placeholder={t('orders.unitPrice')}
                      value={item.unitPrice || ''}
                      onChange={(e) => handleLineItemChange(idx, 'unitPrice', e.target.value)}
                      className="w-24"
                      step="0.01"
                    />
                    <Input
                      type="number"
                      placeholder={t('orders.quantity')}
                      value={item.quantity}
                      onChange={(e) => handleLineItemChange(idx, 'quantity', e.target.value)}
                      className="w-20"
                      min={1}
                    />
                    <Button variant="ghost" size="sm" onClick={() => handleRemoveLineItem(idx)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))}
              </div>

              {/* Add line item actions */}
              <div className="flex gap-2 mt-3">
                <Button variant="outline" size="sm" onClick={handleAddManualItem}>
                  <Wrench className="h-4 w-4 mr-1" />
                  {t('orders.addManualItem')}
                </Button>
                <div className="relative flex-1">
                  <Input
                    placeholder={t('orders.searchProducts')}
                    value={productSearch}
                    onChange={(e) => handleProductSearch(e.target.value)}
                    onFocus={() => { if (productResults.length > 0) setShowProductDropdown(true); }}
                    className="pr-8"
                  />
                  <Package className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  {showProductDropdown && productResults.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {productResults.map((p) => (
                        <button
                          key={p.id}
                          className="w-full text-left px-3 py-2 hover:bg-muted text-sm flex items-center justify-between"
                          onClick={() => handleSelectProduct(p)}
                        >
                          <div>
                            <span className="font-medium">{p.name}</span>
                            {p.sku && <span className="text-muted-foreground ml-2 text-xs">SKU: {p.sku}</span>}
                          </div>
                          <span className="text-emerald-600 font-medium">{p.price}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Line items total */}
              {newOrderLineItems.length > 0 && (
                <div className="flex justify-between mt-3 pt-2 border-t text-sm font-medium">
                  <span>{t('orders.total')}</span>
                  <span>
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
                      newOrderLineItems.reduce((sum, item) => sum + (item.unitPrice || 0) * item.quantity, 0)
                    )}
                  </span>
                </div>
              )}
            </div>

            {/* Fulfillment Type */}
            <div>
              <Label>{t('orders.fulfillment')}</Label>
              <Select
                value={newOrderFulfillment}
                onValueChange={(value) => setNewOrderFulfillment(value as FulfillmentType)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="IN_STORE">{t('orders.inStore')}</SelectItem>
                  <SelectItem value="PICKUP">{t('orders.pickup')}</SelectItem>
                  <SelectItem value="DELIVERY">{t('orders.delivery')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Delivery Details (only when DELIVERY selected) */}
            {newOrderFulfillment === 'DELIVERY' && (
              <div className="space-y-3 p-3 border rounded-md bg-muted/30">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Truck className="h-4 w-4" />
                  Delivery Details
                </div>
                <div>
                  <Label>Delivery Address</Label>
                  <Input
                    placeholder="Rua Example 123, Apt 302, Bloco B"
                    value={newOrderDeliveryAddress}
                    onChange={(e) => setNewOrderDeliveryAddress(e.target.value)}
                  />
                </div>
                <div>
                  <Label>Delivery Notes</Label>
                  <Input
                    placeholder="Deixar com o porteiro, interfone 302"
                    value={newOrderDeliveryNotes}
                    onChange={(e) => setNewOrderDeliveryNotes(e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Portaria Contact</Label>
                    <Input
                      placeholder="Nome do porteiro"
                      value={newOrderDeliveryContactName}
                      onChange={(e) => setNewOrderDeliveryContactName(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Portaria Phone</Label>
                    <Input
                      placeholder="+55 11 99999-9999"
                      value={newOrderDeliveryContactPhone}
                      onChange={(e) => setNewOrderDeliveryContactPhone(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Notes */}
            <div>
              <Label>{t('orders.notes')}</Label>
              <Input
                placeholder={t('orders.notes')}
                value={newOrderNotes}
                onChange={(e) => setNewOrderNotes(e.target.value)}
              />
            </div>
          </div>

          {createError && (
            <div className="bg-destructive/15 text-destructive px-3 py-2 rounded-md text-sm">
              {createError}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { resetCreateDialog(); setCreateError(null); setShowCreateDialog(false); }}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleCreateOrder}
              disabled={createLoading || !newOrderCustomerId || newOrderLineItems.length === 0}
            >
              {createLoading ? t('common.loading') : t('orders.createOrder')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
